import Button from '@/common/components/atom/button/button';
import Modal from '@/common/components/atom/modal/modal';
import Text from '@/common/components/atom/text/text';
import useApplication from '@/common/hooks/useApplication';
import useCustomize from '@/common/hooks/useCustomize';
import useModal from '@/common/hooks/useModal';
import useWebView from '@/common/hooks/useWebView';
import { sendGaEvent } from '@/common/services/sendGaEvent';
import { getDoctorCenterServices } from '@/common/apis/services/doctor/getDoctorCenterServices';
import { reformattedCentersProperty } from '@/modules/booking/functions/reformattedCentersProperty';
import { reformattedServicesProperty } from '@/modules/booking/functions/reformattedServicesProperty';
import { isInvalidProfileServiceId, mapDoctorCenterServices } from '@/modules/booking/functions/mapDoctorCenterServices';
import { memo, useState } from 'react';
import PresenceIcon from '@/common/components/icons/presence';
import ServiceCard from '../../components/serviceCard/serviceCard';
import { useProfileSplunkEvent } from '../../hooks/useProfileEvent';
import orderBy from 'lodash/orderBy';
import SelectService from '@/modules/booking/views/selectService';
import SelectCenter from '@/modules/booking/views/selectCenter';
import { useRouter } from 'next/router';
import { useAbsentScore } from '@/common/apis/services/ravi/absentScore';
import BulkService from './bulk';
import toast from 'react-hot-toast';

interface PresenceProps {
  centers: any[];
  waitingTime?: string;
  onBook: ({ centerId, serviceId }: { centerId: string; serviceId?: string }) => void;
  displayName: string;
  expertises?: any;
  doctorCity?: string;
  doctorId?: string;
  bookingDisabled?: boolean;
}

export const Presence = memo((props: PresenceProps) => {
  const { centers, waitingTime, onBook, displayName, expertises, doctorCity, doctorId, bookingDisabled } = props;
  const router = useRouter();
  const slug = router.query.slug as string;
  const { data: absentScoreData } = useAbsentScore(slug);
  const customise = useCustomize(state => state.customize);
  const isWebView = useWebView();
  const isApplication = useApplication();
  const { profileEvent } = useProfileSplunkEvent();
  const [selectedCenter, setSelectedCenter] = useState<any>({});
  const [isResolvingServices, setIsResolvingServices] = useState(false);

  const penaltyScoreItem = absentScoreData?.list?.find(item => item.penalty_score != null && item.penalty_score !== undefined);
  const penaltyScore = penaltyScoreItem?.penalty_score;


  const needsDrprofileServices = (center: any) => {
    const services = center?.services ?? [];
    if (services.length === 0) return true;
    return services.some((service: any) => isInvalidProfileServiceId(service?.id));
  };

  const resolveCenterServices = async (center: any) => {
    if (!needsDrprofileServices(center) || !slug) {
      return center.services ?? [];
    }

    try {
      setIsResolvingServices(true);
      const items = await getDoctorCenterServices(slug, center.id);
      return mapDoctorCenterServices(items, center.user_center_id);
    } catch (error) {
      toast.error('خطا در دریافت خدمات مرکز درمانی.');
      return center.services ?? [];
    } finally {
      setIsResolvingServices(false);
    }
  };

  const getAlertConfig = () => {
    if (!penaltyScore || penaltyScore === 0) {
      return null;
    }

    if (penaltyScore >= 1) {
      return {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-300',
        textColor: 'text-red-600',
        message: 'طبق نظر مراجعین، "حتما" پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }

    if (penaltyScore > 0.1 && penaltyScore < 1) {
      return {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
        textColor: 'text-black',
        message: 'طبق نظر مراجعین، حتما پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }

    if (penaltyScore <= 0.1) {
      return {
        bgColor: 'bg-[#F2F2F2]',
        borderColor: 'border-gray-300',
        textColor: 'text-black',
        message: 'طبق نظر مراجعین، پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }

    return null;
  };

  const alertConfig = getAlertConfig();
  const {
    handleOpen: handleOpenSelectCenterModal,
    handleClose: handleCloseSelectCenterModal,
    modalProps: selectCenterModalProps,
  } = useModal();

  const {
    handleOpen: handleOpenSelectServiceModal,
    handleClose: handleCloseSelectServiceModal,
    modalProps: selectServiceModalProps,
  } = useModal();
  const { handleOpen: handleOpenSelectExternalBookingModal, modalProps: externalBookingModalProps } = useModal();
  const { handleOpen: handleOpenSelectDownloadAppModal, modalProps: downloadAppModalProps } = useModal();
  const isShowCenterAvailableBox =
    centers[0]?.freeturns_info?.length === centers[0].services.length
      ? centers[0]?.freeturns_info?.every((freeTurn: any) => freeTurn?.available_time > Math.floor(new Date().getTime() / 1000))
      : false;

  const handleOnBook = () => {
    if (bookingDisabled) return;

    // Open UI first — analytics payload building must not delay the click response (INP).
    if (centers.length === 1) {
      const center = centers[0];
      handleOnBookByCenter(center, {
        event: {
          action: 'automatic_select_center',
        },
      });
    } else {
      handleOpenSelectCenterModal();
    }

    sendGaEvent({ action: 'P24DrsPage', category: 'bookButtonStartPresence', label: 'bookButtonStartPresence' });
    sendGaEvent({ action: 'newprofile', category: 'button-book', label: displayName });
    profileEvent('doctor profile press book button', {
      centers_name: centers.map(center => center.name),
      centers_address: centers.map(center => center.address),
      centers_phone: centers.map(center => center.tell),
      centers_city: centers.map(center => center.city),
      centers_statuses: centers.map(center => center.status),
      centers_types: centers.map(center => center.center_type_name),
    });
    if (centers.length > 1) {
      profileEvent('center_selection_modal_load', {
        centers_name: centers.map(center => center.name),
        centers_address: centers.map(center => center.address),
        centers_phone: centers.map(center => center.tell),
        centers_city: centers.map(center => center.city),
        centers_statuses: centers.map(center => center.status),
        centers_types: centers.map(center => center.center_type_name),
      });
    }
  };

  const handleOnBookByCenter = async (center: any, options?: { event: Record<string, string> }) => {
    if (center.is_center === 'mashhad') {
      setSelectedCenter(center);
      handleOpenSelectExternalBookingModal();
      profileEvent('center_selection_modal_click', {
        center_name: center.name,
        center_address: center.address,
        center_phone: center.tell,
        center_city: center.city,
        center_status: center.status,
        center_type: center.center_type_name,
        ...options?.event,
      });
      return;
    }

    if (center.is_only_in_app?.status && !customise.partnerKey && !isWebView && !isApplication) {
      setSelectedCenter(center);
      handleOpenSelectDownloadAppModal();
      profileEvent('center_selection_modal_click', {
        center_name: center.name,
        center_address: center.address,
        center_phone: center.tell,
        center_city: center.city,
        center_status: center.status,
        center_type: center.center_type_name,
        ...options?.event,
      });
      return;
    }

    const needsFetch = needsDrprofileServices(center);
    if (needsFetch) {
      // Show service modal immediately with current (possibly empty) services + loading state.
      setSelectedCenter(center);
      handleCloseSelectCenterModal();
      handleOpenSelectServiceModal();
    }

    const resolvedServices = await resolveCenterServices(center);
    const centerWithServices = { ...center, services: resolvedServices };
    setSelectedCenter(centerWithServices);

    profileEvent('center_selection_modal_click', {
      center_name: center.name,
      center_address: center.address,
      center_phone: center.tell,
      center_city: center.city,
      center_status: center.status,
      center_type: center.center_type_name,
      ...options?.event,
    });

    if (resolvedServices.length === 1) {
      const serviceId = resolvedServices[0]?.id;
      if (!isInvalidProfileServiceId(serviceId)) {
        if (needsFetch) handleCloseSelectServiceModal();
        return onBook({ centerId: center.id, serviceId });
      }
    }

    if (resolvedServices.length === 0) {
      if (needsFetch) handleCloseSelectServiceModal();
      toast.error('خدمتی برای این مرکز یافت نشد.');
      return;
    }

    profileEvent('doctor profile select center button', {
      selected_center_name: center?.name,
      freeturn_text: center?.freeturn_text,
    });

    if (!needsFetch) {
      handleCloseSelectCenterModal();
      handleOpenSelectServiceModal();
    }
  };

  const handleOnBookByService = (service: any) => {
    profileEvent('doctor profile select service button', {
      service_id: service?.id,
      service_alias_title: service?.alias_title,
    });

    handleCloseSelectServiceModal();
    if (service?.id != null && String(service.id) !== '-1') {
      return onBook({ centerId: selectedCenter.id, serviceId: service.id });
    }
    return onBook({ centerId: selectedCenter.id });
  };

  if (centers.length === 1 && !!centers[0].freeturns_info?.length && isShowCenterAvailableBox) {
    return (
      <BulkService
        displayName={displayName}
        expertises={expertises ?? { group_expertises: [], expertises: [] }}
        dcotorCity={doctorCity ?? ''}
        availableTime={centers[0].freeturns_info?.[0] && centers[0].freeturns_info?.[0]?.availalbe_time_text}
        showSubstituteDoctor={false}
      />
    );
  }

  const mainCenterWaitingTime = orderBy(centers, ['isDisable', o => !o.isAvailable])[0]?.waiting_time_info;


  return (
    <div className="flex flex-col space-y-3">
      <ServiceCard
        header={{
          icon: (
            <PresenceIcon />
          ),
          title: 'نوبت اینترنتی و مراجعه حضوری',
        }}
        body={{
          description: [
            'امکان دریافت زودترین نوبت',
            mainCenterWaitingTime?.waiting_time_title &&
            `طبق نظر مراجعین قبلی، میانگین زمان انتظار ویزیت: <strong style="white-space:nowrap">${mainCenterWaitingTime?.waiting_time_title}</strong>`,
          ].filter(Boolean),
        }}
        alert={
          alertConfig ? (
            <div className={`${alertConfig.bgColor} ${alertConfig.borderColor} border rounded-lg p-3`}>
              <Text fontSize="sm" className={alertConfig.textColor}>
                {alertConfig.message}
              </Text>
            </div>
          ) : undefined
        }
        footer={{
          actions: [
            {
              text: centers.every((center: any) => center.is_book_request) ? 'ثبت درخواست نوبت' : 'دریافت نوبت',
              onClick: handleOnBook,
              disabled: bookingDisabled || isResolvingServices,
              bookingCta: true,
              loading: isResolvingServices,
            },
          ],
        }}
      />

      <Modal name="presence-1" title="انتخاب مرکز درمانی" {...selectCenterModalProps} bodyClassName="pl-3">
        {selectCenterModalProps.isOpen && (
          <div className="pl-2 overflow-auto max-h-[28rem]">
            <SelectCenter
              centers={reformattedCentersProperty({ centers, displayName })}
              onSelect={center => handleOnBookByCenter(centers.find(c => c.id === center.id))}
            />
          </div>
        )}
      </Modal>

      <Modal name="presence-2" title="انتخاب خدمت" {...selectServiceModalProps}>
        {selectServiceModalProps.isOpen && (
          <div>
            {isResolvingServices ? (
              <Text fontWeight="medium" className="block py-6 text-center text-slate-500">
                در حال دریافت خدمات…
              </Text>
            ) : (
              <SelectService
                center={selectedCenter}
                services={reformattedServicesProperty({ services: selectedCenter.services, center: selectedCenter })}
                onSelect={handleOnBookByService}
              />
            )}
          </div>
        )}
      </Modal>
      <Modal name="presence-3" title="نوبت دهی اینترنتی و حضوری (غیرفعال)" {...externalBookingModalProps} bodyClassName="space-y-3">
        <Text fontWeight="medium" className="leading-7">
          در حال حاضر نوبت‌دهی اینترنتی این پزشک غیرفعال می‌باشد! شما می‌توانید با کلیک بر روی دکمه زیر، از طریق سایت دانشگاه علوم پزشکی
          مشهد نوبت خود را دریافت نمایید.
        </Text>
        <Button block onClick={() => location.assign('http://nobat.mums.ac.ir/AppointmentSite/')}>
          ورود به سایت دانشگاه علوم پزشکی مشهد
        </Button>
      </Modal>
      <Modal name="presence-4" title="دانلود اپلیکیشن" {...downloadAppModalProps} bodyClassName="space-y-3">
        <Text fontWeight="medium" className="leading-7">
          {selectedCenter?.is_only_in_app?.message}
        </Text>
        <Button block onClick={() => location.assign('/app')}>
          دانلود اپلیکیشن
        </Button>
      </Modal>
    </div>
  );
});

export default Presence;
